import { Client } from "@/Common/Api/Client";
import { FileType } from "@/Common/Models/FileType";
import * as ServerOperations from "@/Common/Operations/operations.server";
import { BaseGetExportableFileFetchUrlResponse } from "../BaseGetExportableFileFetchUrlResponse";
import { ExportableFile, IExportableFile, IExportableFileLoaded } from "../ExportableFile";
import { GenerateFileOptions } from "../IGenerateFileOptions";
import { FileTypeTemporaryFile } from "./FileTypeTemporaryFile";

interface IFileType_WorkSafeBcForm extends IExportableFile {
    formId: string;
    fromPatientDocId?: string;
    fromPatientDocType?: FileType;
    patientContactId?: string;
    patientContactFacetId?: string;
}

class FileTypeWorkSafeBcForm extends ExportableFile implements IFileType_WorkSafeBcForm {
    public canGenerateFileXcom: boolean = true;

    public formId: string;
    public fromPatientDocId: string;
    public fromPatientDocType: FileType;

    public patientContactId: string;
    public patientContactFacetId: string;

    constructor(fields?: IFileType_WorkSafeBcForm) {
        super(FileType.WorkSafeBcForm);
        Object.assign(this, fields);
    }

    public getAttachedObject_ObjectId(): string {
        return this.formId;
    }

    public getCleanCopy(): FileTypeWorkSafeBcForm {
        return new FileTypeWorkSafeBcForm(this);
    }

    public async getMeta(): Promise<number> {
        await this.generateFileIfNeeded({ includeOverlays: true, isExternal: false });
        return this.totalPages;
    }

    protected async generateFileIfNeededXcom(includeOverlays: boolean): Promise<IExportableFileLoaded> {
        return await this.generateFileIfNeeded({ includeOverlays: includeOverlays, isExternal: true });
    }

    protected async generateFileIfNeeded(generateFileOption: GenerateFileOptions): Promise<IExportableFileLoaded> {
        const { includeOverlays = true, isExternal } = generateFileOption;
        let requestedOverlays = (includeOverlays && this.overlayElements) || [];

        const response =
            isExternal
                ? await Client.postAsyncXcom<BaseGetExportableFileFetchUrlResponse>(
                    ServerOperations.Server.ExportableFile_GetWorkSafeBcFormFetchUrl,
                    {
                        OverlayElements: this.serializeOverlayElements(includeOverlays),
                        formId: this.formId,
                        patientContactId: this.patientContactId,
                        patientContactFacetId: this.patientContactFacetId
                    },
                    this.clientTag,
                    this.claimId
                )
                : await Client.postAsync<BaseGetExportableFileFetchUrlResponse>(
                    ServerOperations.Server.ExportableFile_GetWorkSafeBcFormFetchUrl,
                    {
                        OverlayElements: this.serializeOverlayElements(includeOverlays),
                        formId: this.formId,
                        patientContactId: this.patientContactId,
                        patientContactFacetId: this.patientContactFacetId
                    }
                );

        if (!response.isSuccess)
            return;

        this.totalPages = response.data.TotalPages;

        let tempFile = new FileTypeTemporaryFile({
            ObjectId: response.data.ObjectId,
            totalPages: this.totalPages,
            sizeBytes: response.data.SizeBytes,
            date: this.date,
            description: this.description
        });

        tempFile.isExternal = isExternal;
        if (tempFile.isExternal) {
            tempFile.clientTag = this.clientTag;
            tempFile.claimId = this.claimId;
        }

        super.setCache(tempFile, requestedOverlays);

        return {
            OriginalExportableFile: this,
            PregeneratedTemporaryFile: tempFile,
            FetchUrl:
                isExternal
                    ? tempFile.FetchUrlXcom(this.clientTag, this.claimId)
                    : tempFile.FetchUrl
        } as IExportableFileLoaded;
    }

    public static fromWorkSafeBcForm(
        id: string,
        description: string,
        formDate: Date,
        patientContactId: string = null,
        patientContactFacetId: string = null
    ): FileTypeWorkSafeBcForm {
        return new FileTypeWorkSafeBcForm({
            formId: id,
            description: description,
            date: formDate.toString(),
            patientContactFacetId: patientContactFacetId,
            patientContactId: patientContactId
        });
    }
}

export { FileTypeWorkSafeBcForm };
